import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { css } from 'emotion'
import { throttle } from 'lodash'
import { ListMenu, ListMenuButton, Modal, Popover,
   Spacer, Text, View, useWindowResizeSensor } from 'oio-react'
import { Helmet } from 'react-helmet'
import { Link, Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'
import { ArrowDownIcon, BoltIcon, DesignIcon, PageGlyphIcon24,
   PeopleIcon, VisibilityIcon } from 'assets/icons'
import AccountSettings from 'src/apps/Account/apps/Settings'
import Design from 'src/apps/AdminWorkspace/apps/Design'
import ProtoNavButton from 'src/components/Settings/ProtoNavButton'
import ScrollView from 'src/components/utils/ScrollView'
import { useMe, useOrganization } from 'src/graphql/hooks'
import ThemeContext, { Theme } from 'src/libraries/contexts/Theme'
import NotFound from 'src/pages/NotFound'
import Content from './Content'
import Site from './Site'
import People from './People'

const Settings = () => {
   const popoverButton = useRef()
   const [popoverIsOpen, setPopoverIsOpen] = useState(false)
   const [modalIsOpen, setModalIsOpen] = useState(false)

   const location = useLocation()
   const match = useRouteMatch()
   const theme = useContext(ThemeContext)
   const { currentBreakpoint } = useWindowResizeSensor()
   const { me, isLoggedIn, loading: meLoading } = useMe()
   const { refetch: refetchOrganization, organization } = useOrganization()
   const isMobile = currentBreakpoint < 'c'

   // Since the latest organization data matters much more in Settings than the rest of the
   // app, we don't want to rely on the polling to get the latest initiative data. So we force
   // the issue by refetching the organization on each settings url change. We still throttle
   // refetch organization requests however, to prevent an unreasonably high rate of requests.
   const refetchOrganizationThrottled = useCallback(throttle(refetchOrganization, 7000), [])
   useEffect(() => {
      refetchOrganizationThrottled()
   }, [location.pathname])

   if (meLoading) {
      // TODO: Maybe change this to a loader
      return null
   }

   if (!organization.currentUserCanEdit || !isLoggedIn) {
      return <NotFound />
   }

   return (
      <View
         position="fixed"
         top="0px"
         left="0px"
         right="0px"
         bottom="0px"
         backgroundColor={theme.protoSettingsChromeBackgroundColor}>
         <Helmet title={`Settings | ${organization.name}`} />
         <Theme globalNavIsHidden />
         {/* Settings Main Nav */}
         <ScrollView
            display="flex"
            flexFlow="column"
            justifyContent="flex-start[a-b] space-between[c-f]"
            position="absolute"
            top="0px"
            left="0px"
            width="100%[a-b] 270px[c] 300px[d-f]"
            bottom="0px"
            scroll="on">
            <View flex="0 0 auto">
               <View padding="50px 20px">
                  <Spacer size="3" />
                  <Text size="5.5" color="#fff">
                     {organization.name}
                  </Text>
                  <Spacer size="1" />
                  <Text
                     size="4"
                     weight="normal"
                     color="rgba(255,255,255,0.4)"
                     letterSpacing="-0.25px">
                     Admin Workspace
                  </Text>
                  <Spacer size="11" />
                  <Link to="/">
                     <View
                        className={css`
                           transform: scale(1);
                           transition: 400ms;

                           &:hover {
                              background-color: rgba(247, 183, 124, 0.3);
                              transform: scale(1.02);
                           }
                        `}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        padding="0px 30px 0px 15px"
                        height="45px"
                        borderRadius="4px"
                        backgroundColor={theme.protoSettingsHighlightBackgroundColor}>
                        <View marginTop="2px" marginRight="15px">
                           <VisibilityIcon
                              width="16px"
                              height="16px"
                              color={theme.protoSettingsHighlightForegroundColor}
                              strokeWidth="2px"
                           />
                        </View>
                        <Text size="1" weight="medium" color={theme.protoSettingsHighlightForegroundColor}>
                           View Site
                        </Text>
                     </View>
                  </Link>
               </View>
               <ProtoNavButton
                  linkName="Content & Pages"
                  linkTo={`${match.url}/content`}
                  icon={<PageGlyphIcon24 className="icon" width="14px" height="14px" />}
               />
               <ProtoNavButton
                  color="#fff"
                  linkName="Design & Layout"
                  linkTo={`${match.url}/design`}
                  icon={<DesignIcon className="icon" width="18px" height="18px" />}
               />
               <ProtoNavButton
                  color="#fff"
                  linkName="People, Privacy & Roles"
                  linkTo={`${match.url}/people`}
                  icon={<PeopleIcon className="icon" width="14px" height="14px" />}
               />
               <ProtoNavButton
                  color="#fff"
                  linkName="Site Settings"
                  linkTo={`${match.url}/site`}
                  icon={<BoltIcon className="icon" width="16px" height="16px" />}
               />
            </View>
            <View
               flex="0 0 auto"
               display="flex"
               alignItems="flex-end"
               height="120px[d-f]"
               width="100%"
               padding="30px[a-b] 0px 30px[c-f]">
               <View
                  ref={popoverButton}
                  onClick={() => setPopoverIsOpen(true)}
                  style={{ cursor: 'pointer' }}
                  position="relative"
                  display="flex"
                  alignItems="center"
                  width="100%"
                  padding="20px 0px 40px 0px"
                  borderTop="1px solid rgba(255,255,255,0.05)">
                  <View
                     display="flex"
                     justifyContent="center"
                     alignItems="center"
                     width="25px"
                     height="25px"
                     borderRadius="50%"
                     backgroundColor="#98b4b4">
                     <Text size="1" color="#000" weight="bold">
                        {`${me.firstName.charAt(0)}${me.lastName.charAt(0)}`}
                     </Text>
                  </View>
                  <View padding="0px 15px 0px 20px">
                     <Text size="2" weight="medium" color="rgba(255,255,255,0.7)">
                        {me.fullName}
                     </Text>
                  </View>
                  <ArrowDownIcon
                     width="10px"
                     height="10px"
                     color="#aaa"
                     strokeWidth="4px"
                  />
               </View>
               <Popover
                  anchorElement={popoverButton.current}
                  anchorOriginHorizontal="left"
                  anchorOriginVertical="bottom"
                  width="240px"
                  onBodyClick={() => setPopoverIsOpen(false)}
                  open={popoverIsOpen}>
                  <View
                     position="relative"
                     bottom="-2px"
                     float="left"
                     width="100%"
                     borderRadius="6px"
                     backgroundColor="rgba(0,0,0,0.8)">
                     <View padding="0px 5px">
                        <ListMenu
                           buttonTextColor="#fff"
                           dividerLineStyle="1px solid rgba(255,255,255,0.1)">
                           <ListMenuButton
                              onClick={() => setModalIsOpen(true)}
                              name="Profile & Account Settings"
                           />
                           <ListMenuButton
                              linkTo="/account/logout"
                              name="Logout"
                           />
                        </ListMenu>
                     </View>
                  </View>
               </Popover>
            </View>
         </ScrollView>
         {/* Main Content */}
         <Switch>
            {!isMobile && (
               <Redirect
                  exact
                  from={match.url}
                  to={`${match.url}/content`}
               />
            )}
            <Route
               path={`${match.url}/design`}
               component={Design}
            />
            <Route
               path={`${match.url}/site`}
               component={Site}
            />
            <Route
               path={`${match.url}/content`}
               component={Content}
            />
            <Route
               path={`${match.url}/people`}
               component={People}
            />
         </Switch>
         {/* Account Settings Modal */}
         <Modal
            borderRadius="6px"
            width="100%[a-b] 720px[c-f]"
            height="540px"
            onCloseComplete={() => setModalIsOpen(false)}
            onCloseTrigger={() => setModalIsOpen(false)}
            open={modalIsOpen}>
            <AccountSettings
               onCloseButtonClick={() => setModalIsOpen(false)}
            />
         </Modal>
      </View>
   )
}

export default Settings
